import React, { useState, useRef, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, Upload, Download, Eye, Server, Cpu, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import toast from 'react-hot-toast';
import { API_URL } from '../config';
import apiClient from '../services/apiClient';

const ActivationKeysPage = () => {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [notes, setNotes] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // State for displaying activation keys
  const [activationKeys, setActivationKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isUploadExpanded, setIsUploadExpanded] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Set page title
  useEffect(() => {
    document.title = 'Activation Keys | Customer Portal';
  }, []);

  // Load activation keys on mount
  useEffect(() => {
    loadActivationKeys();
  }, []);

  const loadActivationKeys = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/activation-keys/my-keys');
      const keys = response.data || [];
      setActivationKeys(keys);
      // Auto-collapse upload section if user has licenses
      if (keys.length > 0) {
        setIsUploadExpanded(false);
      }
    } catch (error) {
      console.error('Failed to load activation keys:', error);
      toast.error('Failed to load activation keys');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Check file extension
    if (!file.name.endsWith('.xlic')) {
      toast.error('Only .xlic files are allowed');
      setSelectedFile(null);
      return;
    }

    // Check file size (20KB max)
    if (file.size > 20 * 1024) {
      toast.error('File size must be less than 20KB');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    handleFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a .xlic file');
      return;
    }

    setUploading(true);
    setUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (notes) {
        formData.append('notes', notes);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/activation-keys/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Activation key uploaded successfully!');
        setNotes('');
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Reload activation keys
        loadActivationKeys();
      } else {
        toast.error(data.error || 'Failed to upload activation key');
        setUploadError(data.error || 'Failed to upload activation key');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload activation key');
      setUploadError('Failed to upload activation key');
    } finally {
      setUploading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        icon: Clock,
        className: 'bg-amber-50 text-amber-800 border-amber-200',
        label: 'Pending',
      },
      APPROVED: {
        icon: CheckCircle,
        className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        label: 'Approved',
      },
      REJECTED: {
        icon: XCircle,
        className: 'bg-rose-50 text-rose-800 border-rose-200',
        label: 'Rejected',
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-lg border text-xs font-medium ${config.className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleDownload = async (keyId, hostname) => {
    try {
      const response = await apiClient.get(`/activation-keys/download/${keyId}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activation-${hostname}-signed.xlic`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download activation key');
    }
  };

  const viewDetails = (key) => {
    setSelectedKey(key);
    setShowDetailsModal(true);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Activation Keys</h1>
        <p className="mt-1 text-sm text-gray-500">
          Upload activation key requests and download signed activation keys
        </p>
      </div>

      {/* Upload Section - Collapsible */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <button
          onClick={() => setIsUploadExpanded(!isUploadExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Plus className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left">
              <h2 className="text-lg font-semibold text-gray-900">
                {activationKeys.length === 0 ? 'Upload Your First Activation Key Request' : 'Upload New Activation Key Request'}
              </h2>
              <p className="text-sm text-gray-500">
                {isUploadExpanded ? 'Click to collapse' : 'Click to expand upload form'}
              </p>
            </div>
          </div>
          {isUploadExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {isUploadExpanded && (
          <div className="px-6 pb-6 pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-600 mb-4">
              Upload your <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">.xlic</code> activation key file. Maximum file size is 20KB.
            </p>

        <Input
          ref={fileInputRef}
          type="file"
          accept=".xlic"
          onChange={handleFileSelect}
          className="hidden"
        />
        <div 
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
            dragActive 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          
          {selectedFile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm font-medium text-gray-900">{selectedFile.name}</span>
              </div>
              <p className="text-xs text-gray-500">
                {formatBytes(selectedFile.size)}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-sm text-red-600 hover:text-red-700"
              >
                Remove file
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-2">
                Drag and drop your activation key file (.xlic) here
              </p>
              <p className="text-xs text-gray-500 mb-4">or</p>
              <label className="cursor-pointer">
                <span className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 inline-block"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Browse Files
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-4">
                Maximum file size: 20KB | Supported format: .xlic
              </p>
            </>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Add any additional information or special requirements..."
            disabled={uploading}
          />
        </div>

        <button
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          className="mt-4 w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Activation Key
            </>
          )}
        </button>

            {uploadError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{uploadError}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Information Box - Compact */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <p className="text-xs text-blue-800">
          💡 <strong>Quick Tip:</strong> Upload .xlic files • Get email alerts when signed • Download and deploy to your host
        </p>
      </div>

      {/* Activation Keys List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold">My Activation Key Requests</h2>
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            Loading...
          </div>
        ) : activationKeys.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            <Server className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p>No activation key requests yet</p>
            <p className="text-xs mt-1">Upload your first activation key request above</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Hostname
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Hardware
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(() => {
                    const startIndex = (currentPage - 1) * itemsPerPage;
                    const endIndex = startIndex + itemsPerPage;
                    const paginatedKeys = activationKeys.slice(startIndex, endIndex);
                    
                    return paginatedKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {key.hostname || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">{key.filename || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3 text-xs">
                        <div className="flex items-center">
                          <Cpu className="w-3 h-3 mr-1 text-gray-400" />
                          <span>{key.cpuSockets ? `${key.cpuSockets} sockets` : 'N/A'}</span>
                        </div>
                        {key.cpuCores && (
                          <div className="flex items-center">
                            <span>{key.cpuCores} cores</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(key.status)}
                    </td>
                    <td className="px-6 py-4 text-xs text-gray-500">
                      {formatDate(key.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      {key.status === 'APPROVED' && key.signedFilePath ? (
                        <button
                          onClick={() => handleDownload(key.id, key.hostname)}
                          className="inline-flex items-center px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Key
                        </button>
                      ) : key.status === 'REJECTED' && key.responseNotes ? (
                        <div className="text-xs text-red-600">
                          <div className="font-medium">Rejected</div>
                          <div className="text-gray-600">{key.responseNotes}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Processing...</span>
                      )}
                    </td>
                  </tr>
                ));
                  })()}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {activationKeys.length > itemsPerPage && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, activationKeys.length)} to {Math.min(currentPage * itemsPerPage, activationKeys.length)} of {activationKeys.length} requests
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {(() => {
                    const totalPages = Math.ceil(activationKeys.length / itemsPerPage);
                    const pages = [];
                    for (let i = 1; i <= totalPages; i++) {
                      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`px-3 py-1 border rounded text-sm ${
                              currentPage === i
                                ? 'bg-green-600 text-white border-green-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      } else if (i === currentPage - 2 || i === currentPage + 2) {
                        pages.push(<span key={i} className="px-2">...</span>);
                      }
                    }
                    return pages;
                  })()}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(activationKeys.length / itemsPerPage), prev + 1))}
                    disabled={currentPage === Math.ceil(activationKeys.length / itemsPerPage)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Activation Key Details
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Hostname</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedKey.hostname || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedKey.status)}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Product UUID</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono break-all">{selectedKey.productUuid || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Machine ID</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono break-all">{selectedKey.machineId || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">CPU Sockets</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedKey.cpuSockets || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">CPU Cores</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedKey.cpuCores || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Uploaded</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(selectedKey.createdAt)}</p>
                </div>
                {selectedKey.approvedAt && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">Approved</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedKey.approvedAt)}</p>
                  </div>
                )}
              </div>
              {selectedKey.notes && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Your Notes</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedKey.notes}</p>
                </div>
              )}
              {selectedKey.responseNotes && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Admin Response</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedKey.responseNotes}</p>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              {selectedKey.status === 'APPROVED' && selectedKey.signedFilePath && (
                <Button
                  variant="green"
                  size="sm"
                  onClick={() => {
                    handleDownload(selectedKey.id, selectedKey.hostname);
                    setShowDetailsModal(false);
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Signed Key
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowDetailsModal(false)}
                className="text-white bg-gray-500 hover:bg-gray-600"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivationKeysPage;